import { Badge, Switch, Tag, Tooltip } from 'antd';
import type { BadgeProps } from 'antd';
import React from 'react';
import type { ReactNode } from 'react';

import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import type { PictureComponentProps } from 'app/components/commons/PictureComponent/PictureComponent';
import { format as formatDate } from 'app/utils/dayjs';

import './Cells.scss';

export const dateTimeCell =
  (format: string) => (date: string | number | Date) =>
    date && formatDate(new Date(date), format);

export const dateTimeCellMinWidth = (date: string | number | Date) =>
  date && (
    <div style={{ minWidth: 138 }}>
      {formatDate(new Date(date), 'DD/MM/YYYY ')}à
      {formatDate(new Date(date), ' HH:mm')}
    </div>
  );

export const lengthCell = () => (array: unknown[] | null | undefined) =>
  array ? array.length : '0';

export const entityCell =
  (entities: { id: unknown; name: unknown }[]) => (entityId: unknown) =>
    entities.find((entity) => entity.id === entityId)?.name ?? 'Unknown';

export const percentCell = (ratio: number) => `${Math.round(100 * ratio)}%`;

export const boolTextCell =
  (textTrue: string, textFalse: string) => (bool: boolean) =>
    bool ? textTrue : textFalse;

export type OnSwitchCellChange = (entityId: number, newValue: boolean) => void;

type IEntity = {
  id: number;
};

export const SwitchCell =
  <E extends IEntity>(onChange: OnSwitchCellChange) =>
  (checked: boolean, entity: E) =>
    (
      <Switch
        defaultChecked={checked}
        onChange={() => onChange(entity.id, !checked)}
        size="small"
      />
    );

export const SwitchCellWithDisabled =
  <E extends IEntity>(
    onChange: OnSwitchCellChange,
    getDisableStatusTooltip: (e: E) => string | undefined
  ) =>
  (checked: boolean, entity: E) => {
    const tooltip = getDisableStatusTooltip(entity);

    return tooltip ? (
      <Tooltip title={tooltip}>
        <Switch defaultChecked={checked} size="small" disabled />
      </Tooltip>
    ) : (
      <Switch
        defaultChecked={checked}
        onChange={() => onChange(entity.id, !checked)}
        size="small"
        disabled={!!getDisableStatusTooltip(entity)}
      />
    );
  };

export const BadgeCell =
  (badges: (BadgeProps & { value: unknown })[]) => (value: unknown) => {
    const badgeData = badges.find((badge) => badge.value === value);

    return badgeData && <Badge {...badgeData} />;
  };

export const PublishedCell = (published: boolean) =>
  published ? (
    <Badge status="success" text="Published" />
  ) : (
    <Badge status="warning" text="Unpublished" />
  );

export const NewCell = (isNew: boolean) =>
  isNew ? <Tag color="#FF3366">NEW</Tag> : '';

export const StatusCell = (status: string) => {
  switch (status) {
    case 'accepted':
      return (
        <Badge style={{ marginRight: 0 }} status="success" text="Confirmed" />
      ); // GRENN-6
    case 'cancelled':
      return (
        <Badge style={{ marginRight: 0 }} status="error" text="Cancelled" />
      ); // ORANGE-6
    case 'pending':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Pending" />
      ); // GRAY-6
    case 'expired':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Expired" />
      ); // GRAY-6
    case 'aborted':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Aborted" />
      ); // GRAY-6
    case 'rejected':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Rejected" />
      ); // RED-6
    default:
      return status;
  }
};

export const StatusGiftCardBadge = (type: string) => {
  switch (type) {
    case 'active':
      return (
        <Badge style={{ marginRight: 0 }} status="success" text="Active" />
      ); // GRENN-6
    case 'expired':
      return (
        <Badge style={{ marginRight: 0 }} status="warning" text="Expired" />
      ); // ORANGE-6
    case 'empty':
      return <Badge style={{ marginRight: 0 }} status="default" text="Empty" />; // GRAY-6
    case 'rejected':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Rejected" />
      ); // GRAY-6
    case 'pending':
      return (
        <Badge style={{ marginRight: 0 }} status="default" text="Pending" />
      ); // GRAY-6
    default:
      return type;
  }
};

export const StatusGiftCardCell = (type: string) => {
  switch (type) {
    case 'active':
      return (
        <Tag style={{ marginRight: 0 }} color="#52c41a">
          Active
        </Tag>
      ); // GRENN-6
    case 'expired':
      return (
        <Tag style={{ marginRight: 0 }} color="#fa8c16">
          Expired
        </Tag>
      ); // ORANGE-6
    case 'empty':
      return (
        <Tag style={{ marginRight: 0 }} color="#bfbfbf">
          Empty
        </Tag>
      ); // GRAY-6
    case 'rejected':
      return (
        <Tag style={{ marginRight: 0 }} color="#bfbfbf">
          Rejected
        </Tag>
      ); // GRAY-6
    case 'pending':
      return (
        <Tag style={{ marginRight: 0 }} color="#bfbfbf">
          Pending
        </Tag>
      ); // GRAY-6
    default:
      return type;
  }
};

export const CoverCell = (
  name: string,
  entity: { coverPicture: PictureComponentProps }
) => {
  return (
    <div className="cover-cell">
      <div className="cover-cell__thumbnail">
        <PictureComponent {...entity.coverPicture} />
      </div>
      <div className="cover-cell">{name}</div>
    </div>
  );
};

export const CoverCellEmoji = (
  name: string,
  entity: { emoji: PictureComponentProps }
) => {
  return (
    <div className="cover-cell-emoji">
      <div className="cover-cell__thumbnail">
        <PictureComponent {...entity.emoji} />
      </div>
      <div className="cover-cell">{name}</div>
    </div>
  );
};

export const CoverCellCustomFunction =
  (f: (entity: { coverPicture: PictureComponentProps }) => ReactNode) =>
  (value: unknown, entity: { coverPicture: PictureComponentProps }) => {
    return (
      <div className="cover-cell">
        <div className="cover-cell__thumbnail">
          <PictureComponent {...entity.coverPicture} />
        </div>
        <div className="cover-cell">{f(entity)}</div>
      </div>
    );
  };

export const CoverCellRoom = (
  category: string,
  entity: {
    coverPicture: PictureComponentProps & { name?: string };
    name?: string;
  }
) => {
  const { coverPicture, name } = entity;

  return (
    <div className="cover-cell-room">
      <div
        className={`cover-cell-room__thumbnail ${
          coverPicture?.name?.includes('emoji')
            ? 'cover-cell-room__thumbnail-emoji'
            : ''
        }`}
      >
        <PictureComponent {...coverPicture} />
      </div>
      <div className="cover-cell-room__text">
        <div className="cover-cell-room__title">{category}</div>
        {!!name && <div className="cover-cell-room__subtitle">{name}</div>}
      </div>
    </div>
  );
};

export const TagsCell =
  (tags: { id: number; color?: string; name: string }[]) =>
  (tagIds: number[]) =>
    tagIds
      ? tagIds.map((id) => {
          const tag = tags.find((t) => t.id === id);

          if (!tag) {
            return null;
          }

          return (
            <Tag key={id} color={tag.color}>
              {tag.name}
            </Tag>
          );
        })
      : null;
